import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./../Styles/Designes.css";
import { API_BASE_URL, BASE_URL, IMAGE_BASE_URL } from "../../config/api";

const LTC_IMAGE_URL = `${IMAGE_BASE_URL}/`;

export default function SearchDesigns() {
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);

  const searchCode = params.get("code") || "";

  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==============================
  // PAGINATION
  // ==============================

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(40);

  // ==============================
  // CART / WISHLIST
  // ==============================

  const [cartItems, setCartItems] = useState([]);
  const [wishlist, setWishlist] = useState([]);

  // ==============================
  // GET USER
  // ==============================

  const getUser = () => {
    try {
      const user = localStorage.getItem("user");
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  };

  const user = getUser();
  const userId = user?.id;

  // ==============================
  // FETCH SEARCH RESULTS
  // ==============================

  useEffect(() => {
    if (!searchCode) {
      setSearchResults([]);
      setLoading(false);
      return;
    }

    const fetchSearchResults = async () => {
      try {
        setLoading(true);

        const apiUrl = `${BASE_URL}/design-search.php?code=${encodeURIComponent(
          searchCode,
        )}`;

        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const result = await response.json();

        const data = Array.isArray(result?.data) ? result.data : [];

        setSearchResults(data);
        setCurrentPage(1);
      } catch (error) {
        console.error("❌ Search API Error:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [searchCode]);

  // ==============================
  // FETCH CART + WISHLIST
  // ==============================

  useEffect(() => {
    if (!userId) return;

    const fetchUserData = async () => {
      try {
        const [wishlistResponse, cartResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/Getwishlist?user_id=${userId}`),
          fetch(`${API_BASE_URL}/Getcartlist?user_id=${userId}`),
        ]);

        const wishlistData = await wishlistResponse.json();
        const cartData = await cartResponse.json();

        setWishlist(wishlistData.data || []);
        setCartItems(cartData.data || []);
      } catch (error) {
        console.error("Cart/Wishlist error:", error);
      }
    };

    fetchUserData();
  }, [userId]);

  // ==============================
  // IMAGE
  // ==============================

  const getImageUrl = (item) => {
    return item.image_url
      ? `${LTC_IMAGE_URL}${item.image_url}`
      : "/images/no-image.png";
  };

  // ==============================
  // FETCH WISHLIST
  // ==============================

  const fetchWishlist = async () => {
    if (!userId) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/Getwishlist?user_id=${userId}`,
      );

      const result = await response.json();

      setWishlist(result.data || []);
    } catch (error) {
      console.error("Wishlist fetch error:", error);
    }
  };

  // ==============================
  // FETCH CART
  // ==============================

  const fetchCart = async () => {
    if (!userId) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/Getcartlist?user_id=${userId}`,
      );

      const result = await response.json();

      setCartItems(result.data || []);
    } catch (error) {
      console.error("Cart fetch error:", error);
    }
  };

  // ==============================
  // WISHLIST
  // ==============================

  const toggleWishlist = async (design) => {
    if (!userId) {
      alert("Please login");
      return;
    }

    const designId = design.design_id || design.id;

    const existingWishlist = wishlist.find(
      (item) => Number(item.design_id ?? item.id) === Number(designId),
    );

    try {
      if (existingWishlist) {
        const cartId = existingWishlist.cart_id ?? existingWishlist.cartId;

        await fetch(
          `${API_BASE_URL}/RemoveWishlistDesign?cart_id=${cartId}&user_id=${userId}`,
          {
            method: "GET",
          },
        );
      } else {
        await fetch(
          `${API_BASE_URL}/InsertWishlist?user_id=${userId}&design_id=${designId}`,
          {
            method: "GET",
          },
        );
      }

      await fetchWishlist();

      window.dispatchEvent(new Event("wishlistUpdated"));
    } catch (error) {
      console.error("Wishlist error:", error);
    }
  };

  // ==============================
  // ADD / REMOVE CART
  // ==============================

  const addToCart = async (design) => {
    if (!userId) {
      alert("Please login first");
      return;
    }

    const designId = design.design_id || design.id;

    const existingCart = cartItems.find(
      (item) => Number(item.design_id ?? item.id) === Number(designId),
    );

    try {
      if (existingCart) {
        const cartId = existingCart.cartId ?? existingCart.cart_id;

        await fetch(
          `${API_BASE_URL}/RemoveCartDesign?cart_id=${cartId}&user_id=${userId}`,
          {
            method: "GET",
          },
        );
      } else {
        await fetch(
          `${API_BASE_URL}/InsertCartlist?user_id=${userId}&design_id=${designId}`,
          {
            method: "GET",
          },
        );
      }

      await fetchCart();

      window.dispatchEvent(new Event("cartUpdated"));
    } catch (error) {
      console.error("Cart error:", error);
    }
  };

  // ==============================
  // CHECK CART
  // ==============================

  const isInCart = (design) => {
    const designId = design.design_id || design.id;

    return cartItems.some(
      (item) => Number(item.design_id ?? item.id) === Number(designId),
    );
  };

  // ==============================
  // CHECK WISHLIST
  // ==============================

  const isInWishlist = (design) => {
    const designId = design.design_id || design.id;

    return wishlist.some(
      (item) => Number(item.design_id ?? item.id) === Number(designId),
    );
  };

  // ==============================
  // PAGINATION
  // ==============================

  const totalPages = Math.ceil(searchResults.length / itemsPerPage);

  const currentDesigns = searchResults.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // ==============================
  // SCROLL TOP
  // ==============================

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ==============================
  // PAGINATION BLOCK
  // ==============================

  const getPaginationItems = () => {
    const pages = [];

    const blockSize = 10;

    const startPage = Math.floor((currentPage - 1) / blockSize) * blockSize + 1;

    const endPage = Math.min(startPage + blockSize - 1, totalPages);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // ==============================
  // BACK
  // ==============================

  const handleBack = () => {
    const returnUrl = sessionStorage.getItem("searchReturnUrl");

    if (returnUrl) {
      sessionStorage.removeItem("searchReturnUrl");
      navigate(returnUrl);
    } else {
      navigate("/");
    }
  };

  // ==============================
  // LOADING
  // ==============================

  if (loading) {
    return (
      <div className="page-loader">
        <div style={styles.loadingContainer}>
          <div className="spinner-border text-primary mb-3"></div>

          <h5>Searching Designs...</h5>

          <p>Please wait while we find matching designs.</p>
        </div>
      </div>
    );
  }

  // ==============================
  // RENDER
  // ==============================

  return (
    <div className="designs-page container-fluid py-3">
      {/* ==============================
          HEADER
      ============================== */}

      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            <i className="bi bi-arrow-left me-2"></i>
            Back
          </button>

          <h2 className="section-title m-0">Search Results</h2>

          <strong>{searchResults.length} Designs</strong>
        </div>
      </div>

      {/* ==============================
          DESIGNS
      ============================== */}

      <div className="row mx-2">
        <div className="col-12">
          {currentDesigns.length === 0 ? (
            <div className="empty-state text-center py-5">
              <div className="empty-icon mb-3">
                <i className="bi bi-search"></i>
              </div>

              <h4>No designs found</h4>

              <p className="text-muted">No matching designs were found.</p>
            </div>
          ) : (
            <div className="designs-grid">
              {currentDesigns.map((item, index) => (
                <div
                  className="design-card"
                  key={`${item.id}-${item.code}-${index}`}
                  onClick={() =>
                    window.open(
                      `/product?code=${item.code}&cid=${item.cid}&subcid=${item.scid}&icid=${item.icid}`,
                      "_blank",
                    )
                  }
                  style={{
                    cursor: "pointer",
                  }}
                >
                  {/* IMAGE */}

                  <div className="uniq-image-wrapper">
                    <img
                      src={getImageUrl(item)}
                      alt={item.code}
                      className="uniq-design-image"
                    />
                  </div>

                  {/* CARD BODY */}

                  <div className="card-body">
                    <div className="design-specs">
                      {/* LEFT */}

                      <div className="spec-left text-start">
                        <div>
                          <strong>Code:</strong> {item.code}
                        </div>

                        <div>
                          <strong>Stitch:</strong> {item.stiches}
                        </div>

                        <div>
                          <strong>Height:</strong> {item.packageno}
                        </div>

                        <div>
                          <strong>Concept:</strong> {item.concept}
                        </div>
                      </div>

                      {/* RIGHT */}

                      <div className="spec-right text-end">
                        <div>
                          <strong>Area:</strong> {item.area}
                        </div>

                        <div>
                          <strong>Colour:</strong> {item.color}
                        </div>

                        <div>
                          <strong>Width:</strong> {item.sequence}
                        </div>
                      </div>
                    </div>

                    {/* PRICE */}

                    <h5 className="price-tag-row text-center mt-2">
                      <span className="rupee-symbol">₹</span> {item.price}
                    </h5>

                    {/* ==============================
                        CART + WISHLIST
                    ============================== */}

                    <div className="card-action-row">
                      <div className="action-btns">
                        {/* CART */}

                        <button
                          className={`select-btn ${
                            isInCart(item) ? "in-cart" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(item);
                          }}
                        >
                          {isInCart(item) ? (
                            <>
                              <i className="bi bi-cart-check-fill me-1"></i>
                              SELECTED
                            </>
                          ) : (
                            <>
                              <i className="bi bi-cart me-1"></i>
                              SELECT DESIGN
                            </>
                          )}
                        </button>

                        {/* WISHLIST */}

                        <button
                          className={`wish-btn ${
                            isInWishlist(item) ? "active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleWishlist(item);
                          }}
                        >
                          <i
                            className={`bi ${
                              isInWishlist(item) ? "bi-heart-fill" : "bi-heart"
                            }`}
                          ></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ==============================
              PAGINATION
          ============================== */}

          {totalPages > 1 && (
            <div className="pagination-container d-flex justify-content-center mt-5">
              <ul className="pagination custom-pagination">
                {/* PREVIOUS BLOCK */}

                <li
                  className={`page-item ${currentPage <= 10 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => {
                      const startPage =
                        Math.floor((currentPage - 1) / 10) * 10 + 1;

                      if (startPage > 1) {
                        setCurrentPage(startPage - 10);

                        scrollToTop();
                      }
                    }}
                  >
                    Prev
                  </button>
                </li>

                {/* PAGE NUMBERS */}

                {getPaginationItems().map((page) => (
                  <li
                    key={page}
                    className={`page-item ${
                      page === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => {
                        setCurrentPage(page);

                        scrollToTop();
                      }}
                    >
                      {page}
                    </button>
                  </li>
                ))}

                {/* LAST PAGE */}

                {totalPages > 10 &&
                  !getPaginationItems().includes(totalPages) && (
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => {
                          setCurrentPage(totalPages);

                          scrollToTop();
                        }}
                      >
                        {totalPages}
                      </button>
                    </li>
                  )}

                {/* NEXT BLOCK */}

                <li
                  className={`page-item ${
                    Math.floor((currentPage - 1) / 10) * 10 + 11 > totalPages
                      ? "disabled"
                      : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => {
                      const startPage =
                        Math.floor((currentPage - 1) / 10) * 10 + 1;

                      const nextBlockStart = startPage + 10;

                      if (nextBlockStart <= totalPages) {
                        setCurrentPage(nextBlockStart);

                        scrollToTop();
                      }
                    }}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {};
